(function () {
  if (customElements.get('pardus-pickup-point')) return;

  const POSTAL_PREFIX_PROVINCES = {
    '01': ['ALAVA'],
    '02': ['ALBACETE'],
    '03': ['ALICANTE'],
    '04': ['ALMERIA'],
    '05': ['AVILA'],
    '06': ['BADAJOZ'],
    '07': ['I BALEARES', 'ILLES BALEARS', 'BALEARES'],
    '08': ['BARCELONA'],
    '09': ['BURGOS'],
    10: ['CACERES'],
    11: ['CADIZ'],
    12: ['CASTELLON'],
    13: ['CIUDAD REAL'],
    14: ['CORDOBA'],
    15: ['A CORUNA', 'CORUNA'],
    16: ['CUENCA'],
    17: ['GIRONA'],
    18: ['GRANADA'],
    19: ['GUADALAJARA'],
    20: ['GIPUZKOA', 'GUIPUZCOA', 'GUPUZCOA'],
    21: ['HUELVA'],
    22: ['HUESCA'],
    23: ['JAEN'],
    24: ['LEON'],
    25: ['LLEIDA'],
    26: ['LA RIOJA', 'LOGRONO'],
    27: ['LUGO'],
    28: ['MADRID'],
    29: ['MALAGA'],
    30: ['MURCIA'],
    31: ['NAVARRA'],
    32: ['OURENSE', 'ORENSE'],
    33: ['ASTURIAS'],
    34: ['PALENCIA'],
    35: ['I CANARIAS', 'LAS PALMAS'],
    36: ['PONTEVEDRA'],
    37: ['SALAMANCA'],
    38: ['I CANARIAS', 'SANTA CRUZ DE TENERIFE'],
    39: ['CANTABRIA'],
    40: ['SEGOVIA'],
    41: ['SEVILLA'],
    42: ['SORIA'],
    43: ['TARRAGONA'],
    44: ['TERUEL'],
    45: ['TOLEDO'],
    46: ['VALENCIA'],
    47: ['VALLADOLID'],
    48: ['VIZCAYA', 'BIZKAIA'],
    49: ['ZAMORA'],
    50: ['ZARAGOZA'],
    51: ['CEUTA'],
    52: ['MELILLA']
  };

  function normalizeText(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^A-Z0-9]+/gi, ' ')
      .trim()
      .toUpperCase();
  }

  function compactPostalCode(value) {
    return String(value || '').replace(/[\s-]+/g, '').toUpperCase();
  }

  function arrayValue(value) {
    if (Array.isArray(value)) return value;
    return value ? [value] : [];
  }

  // A point serves its `province`, plus any extra provinces listed in `provinces`
  // (e.g. RACEIT covers all of Catalunya: Barcelona, Girona, Lleida, Tarragona).
  function pointProvinceKeys(point) {
    const list = Array.isArray(point.provinces) && point.provinces.length
      ? point.provinces
      : [point.province];
    return list.map(normalizeText).filter(Boolean);
  }

  function postalLooksValid(value) {
    return /^[A-Za-z0-9][A-Za-z0-9\s-]{2,9}$/.test(String(value || '').trim());
  }

  function provinceKeysForPostal(postalCode) {
    const compact = compactPostalCode(postalCode);
    if (/^\d{4}-?\d{3}$/.test(compact)) return ['PORTUGAL'];

    const prefix = compact.match(/^\d{2}/);
    return prefix ? POSTAL_PREFIX_PROVINCES[prefix[0]] || null : null;
  }

  function pointHasPostalData(point) {
    return arrayValue(point.postalCodes).length > 0 || arrayValue(point.postalPrefixes).length > 0;
  }

  function pointMatchesPostal(point, postalCode) {
    const compactPostal = compactPostalCode(postalCode);
    if (!compactPostal) return false;

    const postalCodes = arrayValue(point.postalCodes).map(compactPostalCode);
    if (postalCodes.includes(compactPostal)) return true;

    return arrayValue(point.postalPrefixes)
      .map(compactPostalCode)
      .some((prefix) => prefix && compactPostal.startsWith(prefix));
  }

  class PardusPickupPoint extends HTMLElement {
    connectedCallback() {
      this.points = Array.isArray(window.PardusPickupPoints)
        ? window.PardusPickupPoints.filter((point) => window.PardusPickupLocations?.[point.id])
        : [];
      this.form = this.resolveForm();
      this.details = this.querySelector('[data-pardus-details]');
      this.select = this.querySelector('[data-pardus-select]');
      this.status = this.querySelector('[data-pardus-status]');
      this.selected = this.querySelector('[data-pardus-selected]');
      this.postal = this.querySelector('[data-pardus-postal]');
      this.hiddenInputs = Array.from(this.querySelectorAll('[data-pardus-hidden]'));
      this.availablePoints = [];

      this.setOptions([], this.dataset.emptyLabel || '');
      this.bindEvents();
      this.syncState();

      // The drawer clones a <template>; if this element upgrades before the
      // footer form lands in the tree, resolveForm() misses it. Retry once so
      // submit validation is never silently dropped.
      if (!this.form) {
        requestAnimationFrame(() => {
          if (this.form || !this.isConnected) return;
          this.form = this.resolveForm();
          if (this.form) this.bindFormEvents();
        });
      }
    }

    /*
     * On the product page this element sits inside the form. In the Quick Buy
     * drawer the add-ons render in the scrollable options area while the form
     * stays in the pinned footer, so closest('form') finds nothing — fall back
     * to the id the section stamps on data-form-id. The inputs carry a matching
     * form="" attribute, so they submit either way; this is only about finding
     * the form to hook submit validation onto.
     */
    resolveForm() {
      const inline = this.closest('form');
      if (inline) return inline;

      const formId = this.dataset.formId;
      if (!formId) return null;
      return this.getRootNode().getElementById?.(formId) || document.getElementById(formId);
    }

    bindFormEvents() {
      if (!this.form || this.formEventsBound) return;
      this.formEventsBound = true;
      this.form.addEventListener('submit', (event) => this.handleSubmit(event), true);
    }

    bindEvents() {
      if (this.details) this.details.addEventListener('toggle', () => this.syncState());

      if (this.postal) {
        this.postal.addEventListener('input', () => {
          this.clearFieldError(this.postal);
          this.updateOptionsFromPostal();
        });
        this.postal.addEventListener('change', () => this.validate(false));
      }

      if (this.select) {
        this.select.addEventListener('change', () => {
          this.clearFieldError(this.select);
          this.syncPointProperties();
        });
      }

      this.bindFormEvents();

      document.addEventListener('click', (event) => {
        const addButton = event.target.closest('button[name="add"], input[name="add"], [type="submit"][name="add"]');
        if (!addButton || !this.form || !this.form.contains(addButton)) return;
        if (!this.validate(true)) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }, true);
    }

    handleSubmit(event) {
      if (this.validate(true)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    syncState() {
      const enabled = this.enabled;

      if (this.postal) {
        this.postal.disabled = !enabled;
        this.postal.required = enabled;
        if (!enabled) {
          this.postal.value = '';
          this.clearFieldError(this.postal);
        }
      }

      if (!enabled) {
        this.setOptions([], this.dataset.emptyLabel || '');
        this.hideStatus();
      } else {
        this.updateOptionsFromPostal();
      }

      this.hiddenInputs.forEach((input) => {
        input.disabled = !enabled;
        if (!enabled && input.value !== 'Punto de recogida') input.value = '';
      });

      this.syncPointProperties();
    }

    updateOptionsFromPostal() {
      if (!this.enabled || !this.postal || !this.select) return;

      const postalCode = this.postal.value.trim();

      if (!postalCode || !postalLooksValid(postalCode)) {
        this.setOptions([], this.dataset.emptyLabel || '');
        this.hideStatus();
        return;
      }

      const postalMatches = this.points.filter((point) => pointMatchesPostal(point, postalCode));
      if (postalMatches.length > 0) {
        this.setOptions(postalMatches, this.dataset.selectLabel || '');
        this.showStatus(this.dataset.matchLabel || '');
        return;
      }

      const hasConfiguredPostalData = this.points.some(pointHasPostalData);
      if (hasConfiguredPostalData) {
        this.setOptions(this.points, this.dataset.selectLabel || '');
        this.showStatus(this.dataset.fallbackLabel || '');
        return;
      }

      const provinceKeys = provinceKeysForPostal(postalCode);
      const normalizedKeys = (provinceKeys || []).map(normalizeText);
      const matches = normalizedKeys.length
        ? this.points.filter((point) =>
            pointProvinceKeys(point).some((key) => normalizedKeys.includes(key)))
        : [];

      if (matches.length > 0) {
        this.setOptions(matches, this.dataset.selectLabel || '');
        this.showStatus(this.dataset.matchLabel || '');
      } else {
        this.setOptions(this.points, this.dataset.selectLabel || '');
        this.showStatus(this.dataset.fallbackLabel || '');
      }
    }

    setOptions(points, placeholder) {
      if (!this.select) return;

      const previousValue = this.select.value;
      this.availablePoints = points;
      this.select.innerHTML = '';
      this.select.appendChild(new Option(placeholder, ''));

      points.forEach((point) => {
        const location = [point.address, point.city, point.province].filter(Boolean).join(', ');
        const label = location ? `${point.name} - ${location}` : point.name;
        this.select.appendChild(new Option(label, point.id));
      });

      if (points.some((point) => point.id === previousValue)) {
        this.select.value = previousValue;
      }

      this.select.disabled = !this.enabled || points.length === 0;
      this.select.required = this.enabled && points.length > 0;
      this.clearFieldError(this.select);
      this.syncPointProperties();
    }

    syncPointProperties() {
      const point = this.enabled ? this.selectedPoint : null;

      this.setHiddenValue('[data-pardus-name]', point ? point.name : '');
      this.setHiddenValue('[data-pardus-city]', point ? point.city : '');
      this.setHiddenValue('[data-pardus-province]', point ? point.province : '');
      this.setHiddenValue('[data-pardus-email]', point ? point.email : '');
      this.setHiddenValue('[data-pardus-address]', point ? point.address : '');
      this.setHiddenValue('[data-pardus-id]', point ? point.id : '');

      if (this.selected) {
        const location = point ? [point.address, point.city, point.province].filter(Boolean).join(', ') : '';
        const details = point ? [location, point.email].filter(Boolean).join(' - ') : '';
        this.selected.textContent = point ? `${this.dataset.selectedLabel}: ${details}` : '';
        this.selected.hidden = !point;
      }
    }

    showStatus(message) {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.hidden = !message;
    }

    hideStatus() {
      if (this.status) this.status.hidden = true;
      if (this.selected) this.selected.hidden = true;
    }

    setHiddenValue(selector, value) {
      const input = this.querySelector(selector);
      if (input) input.value = value || '';
    }

    get enabled() {
      return Boolean(this.details && this.details.open);
    }

    get selectedPoint() {
      if (!this.select || !this.select.value) return null;
      return this.availablePoints.find((point) => point.id === this.select.value) || null;
    }

    validate(showFeedback) {
      if (!this.enabled) {
        this.hideFormError();
        return true;
      }

      this.updateOptionsFromPostal();
      let firstInvalid = null;

      if (this.postal) {
        const validPostal = postalLooksValid(this.postal.value);
        this.postal.setCustomValidity(validPostal ? '' : this.dataset.invalidPostal || '');
        if (!validPostal) firstInvalid = firstInvalid || this.postal;
      }

      if (this.select) {
        const validPoint = Boolean(this.selectedPoint);
        this.select.setCustomValidity(validPoint ? '' : this.dataset.invalidPickup || '');
        if (!validPoint) firstInvalid = firstInvalid || this.select;
      }

      const valid = !firstInvalid;

      if (!valid && showFeedback) {
        this.showFormError(firstInvalid.validationMessage);
        firstInvalid.reportValidity();
        firstInvalid.scrollIntoView({ block: 'center', behavior: 'smooth' });
      } else if (valid) {
        this.hideFormError();
      }

      return valid;
    }

    clearFieldError(field) {
      if (field) field.setCustomValidity('');
      this.hideFormError();
    }

    showFormError(message) {
      const error = this.form ? this.form.querySelector('.js-form-error') : null;
      if (!error) return;
      error.textContent = message;
      error.hidden = false;
    }

    hideFormError() {
      const error = this.form ? this.form.querySelector('.js-form-error') : null;
      if (error) error.hidden = true;
    }
  }

  customElements.define('pardus-pickup-point', PardusPickupPoint);
}());
